/**
 * US state display names keyed by uppercase code (aligned with id-tap-that-shared/states/usStates.json + DC).
 */
export const US_STATE_NAMES_BY_CODE = {
  AL: 'Alabama',
  AK: 'Alaska',
  AZ: 'Arizona',
  AR: 'Arkansas',
  CA: 'California',
  CO: 'Colorado',
  CT: 'Connecticut',
  DE: 'Delaware',
  DC: 'District of Columbia',
  FL: 'Florida',
  GA: 'Georgia',
  HI: 'Hawaii',
  ID: 'Idaho',
  IL: 'Illinois',
  IN: 'Indiana',
  IA: 'Iowa',
  KS: 'Kansas',
  KY: 'Kentucky',
  LA: 'Louisiana',
  ME: 'Maine',
  MD: 'Maryland',
  MA: 'Massachusetts',
  MI: 'Michigan',
  MN: 'Minnesota',
  MS: 'Mississippi',
  MO: 'Missouri',
  MT: 'Montana',
  NE: 'Nebraska',
  NV: 'Nevada',
  NH: 'New Hampshire',
  NJ: 'New Jersey',
  NM: 'New Mexico',
  NY: 'New York',
  NC: 'North Carolina',
  ND: 'North Dakota',
  OH: 'Ohio',
  OK: 'Oklahoma',
  OR: 'Oregon',
  PA: 'Pennsylvania',
  RI: 'Rhode Island',
  SC: 'South Carolina',
  SD: 'South Dakota',
  TN: 'Tennessee',
  TX: 'Texas',
  UT: 'Utah',
  VT: 'Vermont',
  VA: 'Virginia',
  WA: 'Washington',
  WV: 'West Virginia',
  WI: 'Wisconsin',
  WY: 'Wyoming',
};

export const BEER_ACTIVITY_MAP_METRICS = {
  reviews: 'reviews',
  checkins: 'checkins',
  avgPerUserDay: 'avgPerUserDay',
};

/**
 * @param {unknown} value
 * @returns {number}
 */
export function normalizeMetricCount(value) {
  if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) {
    return 0;
  }
  return value;
}

/**
 * @param {unknown} metrics
 * @returns {Record<string, Record<string, unknown>>}
 */
export function readByStateMetrics(metrics) {
  if (typeof metrics !== 'object' || metrics === null) {
    return {};
  }
  const byState = metrics.byState;
  if (typeof byState !== 'object' || byState === null) {
    return {};
  }
  return byState;
}

/**
 * @param {Record<string, Record<string, unknown>>} byState
 * @param {string} stateCode
 * @param {string} metricKey
 * @returns {number}
 */
export function getMapMetricValue(byState, stateCode, metricKey) {
  const stateMetrics = byState[stateCode];
  if (typeof stateMetrics !== 'object' || stateMetrics === null) {
    return 0;
  }
  if (metricKey === BEER_ACTIVITY_MAP_METRICS.reviews) {
    return normalizeMetricCount(stateMetrics.beerReviewCount);
  }
  if (metricKey === BEER_ACTIVITY_MAP_METRICS.checkins) {
    return normalizeMetricCount(stateMetrics.beerCheckinCount);
  }
  return normalizeMetricCount(stateMetrics.avgActivitiesPerActiveUserDay);
}

/**
 * @param {Record<string, Record<string, unknown>>} byState
 * @param {string} metricKey
 * @returns {{ min: number, max: number }}
 */
export function getMapMetricRange(byState, metricKey) {
  let minValue = Number.POSITIVE_INFINITY;
  let maxValue = Number.NEGATIVE_INFINITY;

  Object.keys(byState).forEach((stateCode) => {
    const value = getMapMetricValue(byState, stateCode, metricKey);
    if (value < minValue) {
      minValue = value;
    }
    if (value > maxValue) {
      maxValue = value;
    }
  });

  if (!Number.isFinite(minValue) || !Number.isFinite(maxValue)) {
    return { min: 0, max: 0 };
  }
  return { min: minValue, max: maxValue };
}

/**
 * @param {number} value
 * @param {number} minValue
 * @param {number} maxValue
 * @returns {number} 0–1
 */
export function normalizeValueForColorScale(value, minValue, maxValue) {
  if (maxValue <= minValue) {
    return value > 0 ? 1 : 0;
  }
  const normalized = (value - minValue) / (maxValue - minValue);
  return Math.min(1, Math.max(0, normalized));
}

/**
 * @param {Record<string, Record<string, unknown>>} byState
 * @returns {Array<Record<string, unknown>>}
 */
export function buildStateTableRows(byState) {
  const rows = Object.entries(byState).map(([stateCode, stateMetrics]) => {
    const metricsObject =
      typeof stateMetrics === 'object' && stateMetrics !== null ? stateMetrics : {};
    return {
      stateCode,
      stateName: US_STATE_NAMES_BY_CODE[stateCode] ?? stateCode,
      beerCountInDatabase: normalizeMetricCount(metricsObject.beerCountInDatabase),
      beerReviewCount: normalizeMetricCount(metricsObject.beerReviewCount),
      beerCheckinCount: normalizeMetricCount(metricsObject.beerCheckinCount),
      activeUserDays: normalizeMetricCount(metricsObject.activeUserDays),
      avgActivitiesPerActiveUserDay: normalizeMetricCount(
        metricsObject.avgActivitiesPerActiveUserDay,
      ),
      hasBeerInDatabase: Boolean(metricsObject.hasBeerInDatabase),
    };
  });

  return rows.sort((leftRow, rightRow) => leftRow.stateName.localeCompare(rightRow.stateName));
}

/**
 * @param {unknown} summaryBucket
 * @returns {{ activeUserDays: number, avgActivitiesPerActiveUserDay: number }}
 */
export function readSummaryBucket(summaryBucket) {
  if (typeof summaryBucket !== 'object' || summaryBucket === null) {
    return { activeUserDays: 0, avgActivitiesPerActiveUserDay: 0 };
  }
  return {
    activeUserDays: normalizeMetricCount(summaryBucket.activeUserDays),
    avgActivitiesPerActiveUserDay: normalizeMetricCount(
      summaryBucket.avgActivitiesPerActiveUserDay,
    ),
  };
}

/**
 * @param {unknown} metrics
 * @returns {{ withBeer: ReturnType<typeof readSummaryBucket>, withoutBeer: ReturnType<typeof readSummaryBucket> }}
 */
export function readSummaryComparison(metrics) {
  if (typeof metrics !== 'object' || metrics === null) {
    return {
      withBeer: readSummaryBucket(null),
      withoutBeer: readSummaryBucket(null),
    };
  }
  const summary = metrics.summary;
  if (typeof summary !== 'object' || summary === null) {
    return {
      withBeer: readSummaryBucket(null),
      withoutBeer: readSummaryBucket(null),
    };
  }
  return {
    withBeer: readSummaryBucket(summary.statesWithBeer),
    withoutBeer: readSummaryBucket(summary.statesWithoutBeer),
  };
}

/**
 * @param {string} geographyName
 * @returns {string | null}
 */
export function resolveStateCodeFromGeographyName(geographyName) {
  if (typeof geographyName !== 'string' || geographyName.length === 0) {
    return null;
  }
  const entry = Object.entries(US_STATE_NAMES_BY_CODE).find(
    ([, stateName]) => stateName === geographyName,
  );
  return entry ? entry[0] : null;
}
